// File: src/utils/countryNames.js
// Country names in the language the visitor is reading.
//
// CountryContext carries one English name per country, so a Finnish page showed
// "Maa: Finland" and a Swedish one "Land: Finland" - the label translated and
// the value did not.
//
// Rather than hand-maintain sixty strings across fi/en/sv, ask the browser: it
// already ships CLDR's region names for every locale it supports, which is where
// a translator would have copied them from anyway. Any language added later gets
// its country names for free.

import { useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

// Building an Intl.DisplayNames costs real work, and these render inside option
// lists of twenty. One per language, kept for the life of the page.
const formatters = new Map();

const formatterFor = (language) => {
  if (formatters.has(language)) return formatters.get(language);

  let formatter = null;
  try {
    formatter = new Intl.DisplayNames([language], { type: 'region' });
  } catch (err) {
    // No Intl.DisplayNames, or a locale it will not take. The English name we
    // used to show is the fallback, so the worst case is what we had before.
    formatter = null;
  }

  formatters.set(language, formatter);
  return formatter;
};

/**
 * The localized name of a country from CountryContext, falling back to its
 * built-in English name whenever the browser has nothing better.
 */
export const countryName = (country, language) => {
  if (!country) return '';
  const formatter = formatterFor(language);
  if (!formatter) return country.name;

  try {
    const localized = formatter.of(country.code);
    // A well-formed code the browser does not recognise comes back unchanged -
    // "XK" is more use to nobody than the name we already hold.
    return !localized || localized === country.code ? country.name : localized;
  } catch (err) {
    return country.name;
  }
};

/** Same thing, bound to the language currently on screen. */
export function useCountryName() {
  const { language } = useLanguage();
  return useCallback((country) => countryName(country, language), [language]);
}
